import {
  Injectable,
  NgZone,
  OnDestroy,
} from '@angular/core';

import {
  Observable,
  Subject,
} from 'rxjs';

import {
  filter,
  map,
} from 'rxjs/operators';

// This is a ResizeObserver ponyfill from 'https://github.com/juggle/resize-observer'.
// that I'm using since the browser support for this api isn't great currently (https://caniuse.com/#feat=resizeobserver).
// Some of the methods for this library need to be called with runOutsideAngular(), since they are attaching event listeners,
// or calling MutationObserver, which can cause performance issues when run inside of the Angular Zone
// (because of all of the unnecessary change detection).
// There is also another similar polyfill/ponyfill, in case we wanted to swap this one out:
// 'https://github.com/que-etc/resize-observer-polyfill'
import { ResizeObserver } from '@juggle/resize-observer';

import {
  ApDOMRectReadOnly,
  ApResizeObserverEntry,
} from 'webcommon/shared';

// The general logic in this includes:
// 1. handling events outside Angular to avoid overhead of change-detection
// 2. using Subject to handle multicasting
// 3. creating new Observable that subscribes to the Subject for each new element that needs to be observed
//    (which also handles the unsubscribe() from Subject and unobserve())
// This pattern was modeled after ScrollDispatcher in @angular/cdk,
// even though this service performs a completely different task.
@Injectable({
  providedIn: 'root',
})
export class ElementSizingService implements OnDestroy {
  private readonly resizeObserver: ResizeObserver;
  private readonly onResize$ = new Subject<ApResizeObserverEntry>();

  constructor(
    private readonly zone: NgZone,
  ) {
    this.resizeObserver = this.zone.runOutsideAngular(() => {
      return new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          this.onResize$.next(entry);
        });
      });
    });
  }

  sizeChanged(element: Element): Observable<ApDOMRectReadOnly> {
    const newResizeObservable = new Observable<ApDOMRectReadOnly>((observer) => {
      this.zone.runOutsideAngular(() => {
        this.resizeObserver.observe(element);
      });

      const resizeSub = this.onResize$.pipe(
        filter((entry) => entry.target === element),
        map((entry) => entry.contentRect),
      ).subscribe(observer);

      return () => {
        resizeSub.unsubscribe();
        this.resizeObserver.unobserve(element);
      };
    });

    return newResizeObservable;
  }

  ngOnDestroy() {
    this.resizeObserver.disconnect();
    this.onResize$.complete();
  }

}
